import React from 'react';
import { Form } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { RecordType } from '@xbcb/shared-types';
import { useTheme } from 'styled-components';
import {
  EntityRoleCode,
  EntityIdentificationCode,
  CpscCitationCodeType,
} from '__generated__/graphql';
import RecordSelect from 'components/RecordSelect/component';
import LocationSummary from 'components/LocationSummary/component';
import CitationCodes from 'components/CitationCodes/component';
import FormListSection from '../FormListSection';
import {
  StyledSection,
  StyledSectionHeader,
  StyledNamesRow,
  StyledRemoveButton,
  StyledSelect,
  StyledFacilityColumn,
  StyledAddressSummary,
  StyledSwitchRow,
  StyledSwitch,
  StyledAddonInput,
  StyledInput,
  StyledFieldLabel,
  StyledFieldRow,
  StyledTestReportSection,
} from './styles';

const ENTITY_ROLE_CODE_OPTIONS = [
  { value: EntityRoleCode.MF, label: 'MF - Manufacturer of good' },
  {
    value: EntityRoleCode.ITL,
    label: 'ITL - Independent Third Party Laboratory',
  },
  { value: EntityRoleCode.LAB, label: 'LAB - Laboratory' },
  { value: EntityRoleCode.NOL, label: 'NOL - No lab testing required' },
  { value: EntityRoleCode.CE, label: 'CE - Certifying Entity' },
  { value: EntityRoleCode.PK, label: 'PK - Point of Contact' },
];

export interface CpscEntityDetailsProps {
  /**
   * Relative path within Form.List (e.g., [fieldName]).
   * Used for FormItem name binding within the list's scope.
   */
  localNamePath: NamePath;
  /**
   * Absolute path from form root (e.g., ['cpsc', 'items', 0, 'entities', 0]).
   * Required for RecordSelect and Form.useWatch which need full path context.
   */
  fullNamePath: NamePath;
  /** Zero-based entity index; UI displays as 1-based ("Facility Details - 1") */
  index: number;
  /** When true, renders the Remove button. Typically false for index=0 (first entity) */
  showRemove?: boolean;
  /** Callback to remove this entity from the Form.List array */
  onRemove?: () => void;
  /** Disables editing and hides Remove button; fields remain visible but non-interactive */
  readOnly?: boolean;
  /** Grays out and disables all inputs; different from readOnly which hides controls */
  disabled?: boolean;
}

/**
 * CpscEntityDetails - Generic entity component for CPSC eFiling
 *
 * Renders:
 * - Section header with "Entity Details - N" title
 * - Entity Role Code dropdown
 * - Entity Name (Facility picker using RecordSelect)
 * - Address display (when facility is selected)
 * - Remove button (when showRemove is true)
 *
 * Designed to be used with FormListSection for add/remove functionality.
 * @return {JSX.Element} The entity details form section
 */
const CpscEntityDetails: React.FC<CpscEntityDetailsProps> = ({
  localNamePath,
  fullNamePath,
  index,
  showRemove = false,
  onRemove,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();

  // Watch facility field to get selected facility ID for address display
  const facilityPath = [...(fullNamePath as (string | number)[]), 'facility'];
  const facility = Form.useWatch(facilityPath, form);
  const facilityId = facility?.id;

  // Watch entityRoleCode for conditional MF fields
  const entityRoleCodePath = [
    ...(fullNamePath as (string | number)[]),
    'entityRoleCode',
  ];
  const entityRoleCode = Form.useWatch(entityRoleCodePath, form);

  // Watch entityIdentificationCode for SBM toggle state
  const entityIdentificationCodePath = [
    ...(fullNamePath as (string | number)[]),
    'entityIdentificationCode',
  ];
  const entityIdentificationCode = Form.useWatch(
    entityIdentificationCodePath,
    form,
  );

  // Check if MF role is selected
  const isMfRole = entityRoleCode === EntityRoleCode.MF;

  // Check if ITL role is selected (Independent Third Party Laboratory)
  const isItlRole = entityRoleCode === EntityRoleCode.ITL;

  // Check if LAB role is selected (Laboratory)
  const isLabRole = entityRoleCode === EntityRoleCode.LAB;

  // Check if NOL role is selected (No Lab Testing Required)
  const isNolRole = entityRoleCode === EntityRoleCode.NOL;

  // Show Citation Codes section for ITL, LAB (inclusions) and NOL (exclusions) roles
  const showCitationCodesSection = isItlRole || isLabRole || isNolRole;

  // Show Test Reports for ITL and LAB roles
  const showTestReports = isItlRole || isLabRole;

  // Determine if facility picker should be shown
  // ITL and NOL don't need facility picker - ITL uses Lab ID, NOL has no entity details
  const showFacility =
    entityRoleCode !== EntityRoleCode.ITL &&
    entityRoleCode !== EntityRoleCode.NOL;

  // Check if SBM toggle is enabled
  const isSbmEnabled =
    entityIdentificationCode === EntityIdentificationCode.SBM;

  // Handle toggle change for Testing exclusion
  const handleTestingExclusionToggle = (checked: boolean) => {
    if (checked) {
      // Set entityIdentificationCode to SBM when toggle is ON
      form.setFieldValue(
        [...(fullNamePath as (string | number)[]), 'entityIdentificationCode'],
        EntityIdentificationCode.SBM,
      );
    } else {
      // Clear entityIdentificationCode and entityNumber when toggle is OFF
      form.setFieldValue(
        [...(fullNamePath as (string | number)[]), 'entityIdentificationCode'],
        null,
      );
      form.setFieldValue(
        [...(fullNamePath as (string | number)[]), 'entityNumber'],
        null,
      );
    }
  };

  return (
    <StyledSection>
      {/* Section Header */}
      <StyledSectionHeader>
        <span>Facility Details - {index + 1}</span>
        {showRemove && !readOnly && (
          <StyledRemoveButton
            type="text"
            danger
            icon={<MinusCircleOutlined />}
            onClick={onRemove}
            disabled={disabled}
          >
            Remove
          </StyledRemoveButton>
        )}
      </StyledSectionHeader>

      {/* Entity Role Code Dropdown + ITL Laboratory ID */}
      <StyledNamesRow>
        <FormItem
          name={[...(localNamePath as (string | number)[]), 'entityRoleCode']}
          label="Facility Role Code"
          $itemSize={theme.size.short_medium}
          $readOnly={readOnly}
          rules={[{ required: true }]}
          validateTrigger="onBlur"
        >
          <StyledSelect
            data-testid="entity-role-code-select"
            options={ENTITY_ROLE_CODE_OPTIONS}
            disabled={disabled || readOnly}
            placeholder="Select role code"
          />
        </FormItem>

        {/* ITL Conditional Field - Laboratory ID */}
        {isItlRole && (
          <FormItem
            name={[...(localNamePath as (string | number)[]), 'entityNumber']}
            label="Laboratory ID"
            $itemSize={theme.size.short}
            $readOnly={readOnly}
            rules={[{ required: true }]}
            validateTrigger="onBlur"
          >
            <StyledInput
              maxLength={4}
              disabled={disabled || readOnly}
              placeholder="Enter 4-digit CPSC Lab ID"
            />
          </FormItem>
        )}
      </StyledNamesRow>

      {/* MF Conditional Fields - Testing Exclusion Toggle */}
      {isMfRole && (
        <>
          <StyledSwitchRow>
            <StyledSwitch
              data-testid="testing-exclusion-switch"
              checked={isSbmEnabled}
              onChange={handleTestingExclusionToggle}
              disabled={disabled || readOnly}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
            <StyledFieldLabel>Testing exclusion</StyledFieldLabel>
          </StyledSwitchRow>

          {/* SBM Registry ID Input - shown only when toggle is ON */}
          {isSbmEnabled && (
            <StyledNamesRow>
              <FormItem
                name={[
                  ...(localNamePath as (string | number)[]),
                  'entityNumber',
                ]}
                label={
                  <StyledFieldLabel>
                    Small Batch Manufacturer Registry ID
                  </StyledFieldLabel>
                }
                $itemSize={theme.size.short_medium}
                $readOnly={readOnly}
                rules={[{ required: true }]}
                validateTrigger="onBlur"
              >
                <StyledAddonInput
                  addonBefore={EntityIdentificationCode.SBM}
                  maxLength={15}
                  disabled={disabled || readOnly}
                  placeholder="Enter registry ID"
                />
              </FormItem>
            </StyledNamesRow>
          )}
        </>
      )}

      {/* Entity Name (Facility Picker using RecordSelect) - NOT shown for ITL or NOL */}
      {showFacility && (
        <StyledNamesRow>
          <StyledFacilityColumn>
            <Form.Item noStyle>
              <RecordSelect
                recordType={RecordType.FACILITY}
                $itemSize={theme.size.short_medium}
                form={form}
                localNamePath={[
                  ...(localNamePath as (string | number)[]),
                  'facility',
                ]}
                fullNamePath={[
                  ...(fullNamePath as (string | number)[]),
                  'facility',
                ]}
                disabled={disabled}
                label="Facility Name"
                readOnly={readOnly}
                required={true}
                create={true}
                placeHolder="Select Facility"
              />
            </Form.Item>
            {/* Address Display (when facility is selected) */}
            {facilityId && (
              <StyledAddressSummary>
                <LocationSummary recordId={facilityId} />
              </StyledAddressSummary>
            )}
          </StyledFacilityColumn>
        </StyledNamesRow>
      )}

      {/* Testing citation codes - shown for ITL, LAB (inclusions) and NOL (exclusions) */}
      {showCitationCodesSection && (
        <StyledNamesRow>
          <CitationCodes
            codeType={
              isNolRole
                ? CpscCitationCodeType.EXCLUSION
                : CpscCitationCodeType.INCLUSION
            }
            fullNamePath={[
              ...(fullNamePath as (string | number)[]),
              isNolRole ? 'testingExclusions' : 'testingInclusions',
            ]}
            localNamePath={[
              ...(localNamePath as (string | number)[]),
              isNolRole ? 'testingExclusions' : 'testingInclusions',
            ]}
            label={
              <StyledFieldLabel>
                {isNolRole
                  ? 'Testing exclusion citation codes'
                  : 'Testing inclusion citation codes'}
              </StyledFieldLabel>
            }
            placeholder={
              isNolRole
                ? 'Enter testing exclusion citation code and press Enter'
                : 'Enter testing inclusion citation code and press Enter'
            }
            readOnly={readOnly}
            disabled={disabled}
            required={true}
          />
        </StyledNamesRow>
      )}

      {/* Test Reports (testReports array) - shown for ITL and LAB roles */}
      {showTestReports && (
        <StyledTestReportSection>
          <FormListSection
            name={[...(localNamePath as (string | number)[]), 'testReports']}
            addButtonLabel="Add Lab Test Report"
            readOnly={readOnly}
            disabled={disabled}
            renderRow={(field, rowIndex, { remove, showRemove: canRemove }) => (
              <StyledFieldRow key={field.key}>
                <FormItem
                  name={[field.name, 'reportId']}
                  label="Lab Test Report ID (optional)"
                  $itemSize={theme.size.short}
                  $readOnly={readOnly}
                >
                  <StyledInput
                    maxLength={72}
                    disabled={disabled || readOnly}
                    placeholder="Enter report ID"
                  />
                </FormItem>
                <FormItem
                  name={[field.name, 'reportUrl']}
                  label="Lab Test Report URL (optional)"
                  $itemSize={theme.size.short}
                  $readOnly={readOnly}
                >
                  <StyledInput
                    maxLength={72}
                    disabled={disabled || readOnly}
                    placeholder="Enter URL"
                  />
                </FormItem>
                <FormItem
                  name={[field.name, 'accessKey']}
                  label="Access Key (optional)"
                  $itemSize={theme.size.short}
                  $readOnly={readOnly}
                >
                  <StyledInput
                    maxLength={72}
                    disabled={disabled || readOnly}
                    placeholder="Enter access key"
                  />
                </FormItem>
                {canRemove && (
                  <StyledRemoveButton
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={remove}
                    disabled={disabled}
                  />
                )}
              </StyledFieldRow>
            )}
          />
        </StyledTestReportSection>
      )}
    </StyledSection>
  );
};

export default CpscEntityDetails;
